export enum OmraderQuery {
    FYLKE = 'fylke',
    KOMMUNE = 'kommune',
    KONTRAKTSOMRADE = 'kontraktsomrade',
    RIKSVEGRUTE = 'riksvegrute',
    VEGSYSTEMREFERANSE = 'vegsystemreferanse',
    POLYGON = 'polygon',
}

const OMRADER_QUERY_KEYS: OmraderQuery[] = [
    OmraderQuery.FYLKE,
    OmraderQuery.KOMMUNE,
    OmraderQuery.KONTRAKTSOMRADE,
    OmraderQuery.RIKSVEGRUTE,
    OmraderQuery.VEGSYSTEMREFERANSE,
    OmraderQuery.POLYGON,
];

export class OmraderQueries {
    constructor(
        readonly fylke: string[] = [],
        readonly kommune: string[] = [],
        readonly kontraktsomrade: string[] = [],
        readonly riksvegrute: string[] = [],
        readonly vegsystemreferanse: string[] = [],

        readonly polygon: string | null = null
    ) {}

    override(modifyObject: {
        [P in keyof OmraderQueries]?: OmraderQueries[P];
    }): OmraderQueries {
        return Object.assign(Object.create(OmraderQueries.prototype), {
            ...this,
            ...modifyObject,
        });
    }

    withFylker(fylke: string[]) {
        return this.override({ fylke });
    }

    withKommuner(kommune: string[]) {
        return this.override({ kommune });
    }

    withKontraktsomrader(kontraktsomrade: string[]) {
        return this.override({ kontraktsomrade });
    }

    withRiksvegruter(riksvegrute: string[]) {
        return this.override({ riksvegrute });
    }

    withVegsystemreferanser(vegsystemreferanse: string[]) {
        return this.override({ vegsystemreferanse });
    }

    withPolygon(polygon: string | null) {
        return this.override({ polygon });
    }

    split(): OmraderQueries[] {
        const queries: OmraderQueries[] = [];

        if (this.fylke?.length > 0) {
            queries.push(
                new OmraderQueries()
                    .withFylker(this.fylke)
                    .withVegsystemreferanser(this.vegsystemreferanse)
            );
        }

        if (this.kommune?.length > 0) {
            queries.push(
                new OmraderQueries()
                    .withKommuner(this.kommune)
                    .withVegsystemreferanser(this.vegsystemreferanse)
                    .withPolygon(this.polygon)
            );
        }

        if (this.kontraktsomrade?.length > 0) {
            queries.push(
                new OmraderQueries()
                    .withKontraktsomrader(this.kontraktsomrade)
                    .withVegsystemreferanser(this.vegsystemreferanse)
                    .withPolygon(this.polygon)
            );
        }

        if (this.riksvegrute?.length > 0) {
            queries.push(
                new OmraderQueries()
                    .withRiksvegruter(this.riksvegrute)
                    .withVegsystemreferanser(this.vegsystemreferanse)
                    .withPolygon(this.polygon)
            );
        }
        if (this.polygon) {
            queries.push(
                new OmraderQueries()
                    .withVegsystemreferanser(this.vegsystemreferanse)
                    .withPolygon(this.polygon)
            );
        }

        if (queries.length == 0 && this.vegsystemreferanse?.length > 0) {
            queries.push(
                new OmraderQueries().withVegsystemreferanser(this.vegsystemreferanse)
            );
        }

        return queries;
    }

    private valueFor(key: OmraderQuery): string[] | string | null {
        switch (key) {
            case OmraderQuery.FYLKE:
                return this.fylke;
            case OmraderQuery.KOMMUNE:
                return this.kommune;
            case OmraderQuery.KONTRAKTSOMRADE:
                return this.kontraktsomrade;
            case OmraderQuery.RIKSVEGRUTE:
                return this.riksvegrute;
            case OmraderQuery.VEGSYSTEMREFERANSE:
                return this.vegsystemreferanse;
            case OmraderQuery.POLYGON:
                return this.polygon;
        }
    }

    areasLength(): number {
        let length = 0;
        for (const key of OMRADER_QUERY_KEYS) {
            const value = this.valueFor(key);
            if (key === OmraderQuery.POLYGON) {
                length += this.polygon ? 1 : 0;
            } else if (key !== OmraderQuery.VEGSYSTEMREFERANSE) {
                length += typeof value === 'string' || value === null ? 0 : value.length;
            }
        }
        return length;
    }

    toParams(omrader?: OmraderQuery[]): Record<string, string> {
        const params: Record<string, string> = {};

        const keys = omrader ?? OMRADER_QUERY_KEYS;

        keys.forEach(key => {
            const value = this.valueFor(key);
            if (value instanceof Array && value.length > 0) {
                params[key] = value.join();
            } else if (typeof value === 'string' && value !== '') {
                params[key] = value;
            }
        });

        return params;
    }
}
